import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState, useEffect, useRef } from 'react';
import {
  useSite,
  useMigrateSiteDb,
  useDatabaseConnections,
  useCreateDatabaseConnection,
  useTestDatabaseConnection,
  useMigrationStatus,
  useMigrationPreflight,
  useAuthStore,
  sitesApi,
  ApiError,
  useCancelMigration,
  dbConnectionKeys,
  siteKeys,
} from '@wordpuppi/shared';
import type { CreateDatabaseConnectionRequest } from '@wordpuppi/shared';
import { useQueryClient } from '@tanstack/react-query';
import { confirmDialog } from '@/lib/dialog';
import { LoadingSkeleton } from '@/components/LoadingSkeleton';
import { MigrationReview } from '@/components/MigrationReview';
import { UnsavedGuard } from '@/components/UnsavedGuard';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';

export const Route = createFileRoute('/sites/$siteSlug/migrate')({
  component: MigratePage,
});

type DbType = 'postgres' | 'mysql' | 'sqlite';

function MigratePage() {
  const { siteSlug } = Route.useParams();
  const actor = useAuthStore((state) => state.user?.pid ?? '');
  return <SiteMigration key={`${actor}:${siteSlug}`} siteSlug={siteSlug} actor={actor} />;
}
function SiteMigration({ siteSlug, actor }: { siteSlug: string; actor: string }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: siteData, isLoading: siteLoading } = useSite(siteSlug);
  const { data: connectionsData, isLoading: connectionsLoading, refetch: refetchConnections } =
    useDatabaseConnections(siteSlug);
  const migrateSiteDb = useMigrateSiteDb();
  const createConnection = useCreateDatabaseConnection();
  const testConnection = useTestDatabaseConnection();
  const cancelMigration = useCancelMigration(siteSlug);

  // Selected connection (null = none, 'internal-sqlite' = internal SQLite, number = saved connection)
  const [selectedConnectionId, setSelectedConnectionId] = useState<
    number | 'internal-sqlite' | null
  >(null);

  // Migration progress tracking. The query is always on: a migration started in
  // another tab (or before a reload) still owns this page, so `running` has to
  // come from the server, not from local state. The hook only polls while the
  // job is pending/running, so this is one request otherwise.
  // `myJobId` is the job THIS client started — only it fires the toasts, and it
  // also keeps a stale cached "completed" from an earlier job from firing them.
  const startingRef = useRef(false);
  const [myJobId, setMyJobId] = useState<number | null>(null);
  const { data: migrationStatus, error: statusError } = useMigrationStatus(siteSlug, true);
  const status = migrationStatus?.data?.status;
  const running = status === 'pending' || status === 'running';
  const progressKnown = status === 'completed' || (migrationStatus?.data?.total_rows_expected ?? 0) > 0;

  // Handle migration completion
  useEffect(() => {
    const job = migrationStatus?.data;
    if (!job || myJobId === null || job.job_id !== myJobId) return;

    if (job.status === 'completed') {
      setMyJobId(null);
      toast.success(
        `Migration complete: ${job.total_rows_migrated?.toLocaleString() ?? 0} rows migrated`
      );
      if (job.error_message) {
        // Completed-with-warning = a destructive step half-happened. Never
        // auto-dismiss it — we navigate away on the next line.
        toast.warning(job.error_message, { duration: Infinity, closeButton: true });
      }
      queryClient.invalidateQueries({ queryKey: siteKeys.detail(siteSlug) });
      queryClient.invalidateQueries({ queryKey: siteKeys.lists() });
      navigate({ to: `/sites/${siteSlug}/settings` });
    } else if (job.status === 'failed') {
      setMyJobId(null);
      toast.error(job.error_message || 'Migration failed');
    } else if (job.status === 'cancelled') {
      setMyJobId(null);
      toast.info('Migration cancelled');
    }
  }, [migrationStatus, myJobId, navigate, siteSlug, queryClient]);

  // Add database modal state
  const [showAddModal, setShowAddModal] = useState(false);
  const [newDbName, setNewDbName] = useState('');
  const [newDbType, setNewDbType] = useState<DbType>('postgres');
  const [connectionMode, setConnectionMode] = useState<
    'supabase' | 'connection_string' | 'connection_string_ssh'
  >('supabase');
  const [connectionString, setConnectionString] = useState('');
  const [projectRef, setProjectRef] = useState('');
  const [password, setPassword] = useState('');
  const [region, setRegion] = useState('us-east-1');
  const [usePooler, setUsePooler] = useState(true);

  // SSH state
  const [sshHost, setSshHost] = useState('');
  const [sshPort, setSshPort] = useState('22');
  const [sshUsername, setSshUsername] = useState('');
  const [sshPassword, setSshPassword] = useState('');

  // Database provisioning state
  const [databaseName, setDatabaseName] = useState('');
  const [createDbIfNotExists, setCreateDbIfNotExists] = useState(true);
  const [clearExistingDb, setClearExistingDb] = useState(false);
  const preparingConnection = useRef(false);
  const [wipeConfirmation, setWipeConfirmation] = useState('');
  const [maintenanceAck, setMaintenanceAck] = useState(false);
  const [writersAck, setWritersAck] = useState(false);
  const [uncertainStart, setUncertainStart] = useState(false);
  const alive = useRef(true);
  useEffect(() => { alive.current = true; return () => { alive.current = false; }; }, []);
  useEffect(() => { setMaintenanceAck(false); setWritersAck(false); }, [selectedConnectionId]);
  useEffect(() => { setClearExistingDb(false); setWipeConfirmation(''); }, [connectionMode, newDbType, databaseName]);
  const reviewRequest = { operation: 'migrate' as const, ...(selectedConnectionId === 'internal-sqlite' ? { target_db_type: 'sqlite' as const } : typeof selectedConnectionId === 'number' ? { database_connection_id: selectedConnectionId } : {}) };
  const preflight = useMigrationPreflight(siteSlug, reviewRequest, actor, true, siteData?.data?.account_pid ?? '');
  const review = preflight.data?.data;
  const context = JSON.stringify([actor, siteSlug, siteData?.data?.account_pid, selectedConnectionId]);
  const latestContext = useRef(context); latestContext.current = context;

  if (siteLoading || connectionsLoading) {
    return <LoadingSkeleton />;
  }

  if (!siteData?.data) {
    return <div className="text-center py-12 text-muted-foreground">Site not found</div>;
  }

  const site = siteData.data;
  const connections = connectionsData?.data ?? [];
  const selectedConnection = connections.find(
    (c) => c.id === selectedConnectionId
  );

  // Build preview URL for Supabase connection
  const supabasePreviewUrl =
    connectionMode === 'supabase' && projectRef
      ? `postgres://postgres.${projectRef}:****@${
          usePooler
            ? `aws-0-${region}.pooler.supabase.com:5432`
            : `db.${projectRef}.supabase.co:5432`
        }/postgres`
      : '';

  const handleMigrate = async () => {
    const reviewedFingerprint = review?.review_fingerprint;
    if (!reviewedFingerprint) return;
    if (startingRef.current || running || myJobId !== null || !alive.current || !maintenanceAck || (review?.maintenance.external_writers_ack_required !== false && !writersAck)) return;
    if (!selectedConnectionId) {
      toast.error('Please select a database connection');
      return;
    }

    const startedContext = context;
    let submitted = false;
    startingRef.current = true;
    try {
      const current = await sitesApi.getMigrationStatus(siteSlug);
      if (!alive.current || latestContext.current !== startedContext) return;
      if (!current.success || !current.data?.success || ['pending', 'running'].includes(current.data.status ?? '')) throw new Error('Review the current migration status before starting another job.');
      const checked = await sitesApi.migrationPreflight(siteSlug, reviewRequest);
      if (!alive.current || latestContext.current !== startedContext) return;
      if (!checked.success || !checked.data?.can_execute || !checked.data.permissions.migrate) { await preflight.refetch(); throw new Error('Migration is blocked by the current impact review.'); }
      if (checked.data.review_fingerprint !== reviewedFingerprint) { setMaintenanceAck(false); setWritersAck(false); await Promise.all([preflight.refetch(), refetchConnections()]); throw new Error('The reviewed source or destination changed.'); }
      setUncertainStart(true); submitted = true;
      const result = await migrateSiteDb.mutateAsync({
        slug: siteSlug,
        data:
          selectedConnectionId === 'internal-sqlite'
            ? { target_db_type: 'sqlite', maintenance_acknowledged: maintenanceAck, external_writers_quiesced: writersAck, reviewed_fingerprint: reviewedFingerprint }
            : { database_connection_id: selectedConnectionId, maintenance_acknowledged: maintenanceAck, external_writers_quiesced: writersAck, reviewed_fingerprint: reviewedFingerprint },
      });

      if (!alive.current || latestContext.current !== startedContext) return;
      if (result.success && result.data?.success && result.data.job_id) {
        setUncertainStart(false);
        // Own this job: the status query is already polling.
        setMyJobId(result.data.job_id);
        toast.info('Migration started');
      } else {
        setUncertainStart(false);
        toast.error(result.data?.message || 'Migration failed');
      }
    } catch (err) {
      if (alive.current && latestContext.current === startedContext) {
        if (err instanceof ApiError && err.status === 409) { setUncertainStart(false); setMaintenanceAck(false); setWritersAck(false); toast.error('The reviewed source, destination or job changed. Refresh status and review again before starting.'); await Promise.all([preflight.refetch(), refetchConnections()]); }
        else toast.error(submitted ? 'Start was not confirmed. Refresh durable migration status before retrying.' : 'Migration was not requested. Refresh the impact review and job status.');
        await queryClient.invalidateQueries({ queryKey: siteKeys.migrationStatus(siteSlug) });
      }
    } finally { startingRef.current = false; }
  };

  const handleCancelMigration = async () => {
    const expectedJobId = migrationStatus?.data?.job_id;
    if (expectedJobId === undefined) return;
    try {
      const current = await sitesApi.getMigrationStatus(siteSlug);
      if (!alive.current) return;
      if (!current.success || !current.data?.success || current.data.job_id !== migrationStatus?.data?.job_id || !['pending', 'running'].includes(current.data.status ?? '')) throw new Error('Migration status changed. Refresh before cancelling.');
      const result = await cancelMigration.mutateAsync(expectedJobId);
      if (!result.success || !result.data?.success) {
        throw new Error(result.data?.message || 'Failed to cancel');
      }
      toast.info('Cancellation requested');
    } catch (err) {
      toast.error(`${err instanceof Error ? err.message : 'Cancellation was not confirmed'}. Check durable status before retrying; already copied rows may remain.`);
      void queryClient.invalidateQueries({ queryKey: siteKeys.migrationStatus(siteSlug) });
    }
  };

  const handleAddDatabase = async () => {
    if (newDbType === 'sqlite') { toast.error('Use built-in SQLite on the hosting server instead of saving a file profile.'); return; }
    if (preparingConnection.current) return;
    preparingConnection.current = true;
    try {
    if (!review?.permissions.manage_destinations || !alive.current) return;
    if (databaseName && !/^[A-Za-z0-9_-]{1,63}$/.test(databaseName)) { toast.error('Database name must be 1–63 letters, digits, underscores or hyphens.'); return; }
    if (clearExistingDb && (!databaseName.trim() || wipeConfirmation !== databaseName)) { toast.error('Type the exact database name to authorize replacement.'); return; }
    if (clearExistingDb && !(await confirmDialog(`Permanently replace database ${databaseName}? This deletes its contents before saving the connection and cannot be undone. A content export is not a complete backup. The server refuses a target containing WordPuppi or form tables.`, { destructive: true, confirmLabel: 'Replace database' }))) return;
    if (!alive.current) return;
    if (!newDbName.trim()) {
      toast.error('Please enter a name for this connection');
      return;
    }

    const data: CreateDatabaseConnectionRequest = {
      name: newDbName.trim(),
      db_type: newDbType,
    };

    {
      if (connectionMode === 'supabase') {
        data.supabase_project_ref = projectRef;
        data.supabase_password = password;
        data.supabase_region = region;
        data.supabase_use_pooler = usePooler;
      } else if (connectionMode === 'connection_string_ssh') {
        data.connection_string = connectionString;
        data.ssh_enabled = true;
        data.ssh_host = sshHost;
        data.ssh_port = parseInt(sshPort, 10) || 22;
        data.ssh_username = sshUsername;
        data.ssh_password = sshPassword;
      } else {
        data.connection_string = connectionString;
      }

      if (databaseName) {
        data.database_name = databaseName;
        if (connectionMode === 'connection_string') {
          data.create_db_if_not_exists = createDbIfNotExists;
          data.clear_existing_db = clearExistingDb;
          if (clearExistingDb) data.confirm_clear_database_name = wipeConfirmation;
        }
      }
    }

    try {
      const result = await createConnection.mutateAsync({
        siteSlug,
        data,
      });

      toast.success(`Database connection "${newDbName}" created`);
      setShowAddModal(false);
      await queryClient.refetchQueries({ queryKey: dbConnectionKeys.list(siteSlug) });
      setSelectedConnectionId(result.data!.id);

      // Reset form
      setNewDbName('');
      setProjectRef('');
      setPassword('');
      setConnectionString('');
      setSshHost('');
      setSshPort('22');
      setSshUsername('');
      setSshPassword('');
      setDatabaseName('');
      setCreateDbIfNotExists(true);
      setClearExistingDb(false);
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : 'Failed to create connection'
      );
    }
    } finally { preparingConnection.current = false; }
  };

  const handleDropdownChange = (value: string) => {
    if (value === 'add-new') {
      if (review?.permissions.manage_destinations) setShowAddModal(true);
    } else if (value === 'internal-sqlite') {
      setSelectedConnectionId('internal-sqlite');
    } else {
      setSelectedConnectionId(value ? parseInt(value, 10) : null);
    }
  };

  const handleTestConnection = async () => {
    if (!selectedConnectionId || selectedConnectionId === 'internal-sqlite') return;
    try {
      const result = await testConnection.mutateAsync({
        siteSlug,
        id: selectedConnectionId,
      });
      if (result.data?.healthy) {
        toast.success('Connection successful!');
      } else {
        toast.error(result.data?.message || 'Connection test failed');
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Connection test failed');
    }
  };

  return (
    <div className="max-w-xl">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold">Migrate Database</h1>
        <p className="text-muted-foreground mt-2">
          Migrate content from {site.db_type.toUpperCase()} to a new database
        </p>
      </div>

      <div className="space-y-4">
        <div className="space-y-2">
          <Label>Target Database</Label>
          <Select
            value={selectedConnectionId?.toString() ?? ''}
            onValueChange={handleDropdownChange}
            disabled={running || migrateSiteDb.isPending || myJobId !== null}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select a database...">
                {selectedConnectionId === 'internal-sqlite'
                  ? 'SQLite on the hosting server'
                  : selectedConnection
                    ? `${selectedConnection.name} (${selectedConnection.db_type})`
                    : 'Select a database...'}
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              {site.db_type !== 'sqlite' && (
                <SelectItem value="internal-sqlite">SQLite on the hosting server</SelectItem>
              )}
              {connections.map((conn) => (
                <SelectItem key={conn.id} value={conn.id.toString()} disabled={conn.db_type === 'sqlite'}>
                  {conn.name} ({conn.db_type}){conn.db_type === 'sqlite' ? ' — use hosting-server SQLite instead' : ''}
                </SelectItem>
              ))}
              <SelectItem value="add-new" disabled={!review?.permissions.manage_destinations}>+ Add a database...</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {selectedConnectionId === 'internal-sqlite' && (
          <div className="bg-muted p-3 rounded-md text-sm text-muted-foreground">
            A SQLite database on the hosting server. No phone, tablet or browser is a database destination.
          </div>
        )}

        {selectedConnection && (
          <div className="bg-muted p-3 rounded-md text-sm">
            <div className="text-muted-foreground mb-1">Connection:</div>
            <div className="font-mono break-all">{selectedConnection.connection_preview}</div>
            {selectedConnection.ssh_enabled && (
              <div className="mt-2 text-muted-foreground">
                SSH: {selectedConnection.ssh_username}@{selectedConnection.ssh_host}
                {selectedConnection.ssh_port !== 22 && `:${selectedConnection.ssh_port}`}
              </div>
            )}
            <Button
              variant="secondary"
              size="sm"
              className="mt-3"
              onClick={handleTestConnection}
              disabled={testConnection.isPending}
            >
              {testConnection.isPending ? 'Testing...' : 'Test Connection'}
            </Button>
          </div>
        )}

        <MigrationReview data={review} loading={preflight.isFetching} error={!!preflight.error || preflight.data?.success === false} refresh={() => void preflight.refetch()} />
        {uncertainStart ? <p role="alert" className="text-sm">The last start was not confirmed. Check the durable job status before retrying; leaving the app does not cancel an accepted job.</p> : null}
        <Button variant="outline" onClick={() => void queryClient.invalidateQueries({ queryKey: siteKeys.migrationStatus(siteSlug) })}>Refresh migration status</Button>
        <label className="flex items-start gap-2 text-sm"><Checkbox checked={maintenanceAck} onCheckedChange={(value) => setMaintenanceAck(value === true)} disabled={running} />I understand editing and submission intake may be unavailable during maintenance.</label>
        {review?.maintenance.external_writers_ack_required !== false ? <label className="flex items-start gap-2 text-sm"><Checkbox checked={writersAck} onCheckedChange={(value) => setWritersAck(value === true)} disabled={running} />I have paused external writers to the source and destination.</label> : null}
        {/* Migration Progress UI */}
        {(statusError || migrationStatus?.data?.success === false) && (
          <p role="alert" className="text-destructive text-sm">
            {statusError?.message || migrationStatus?.data?.message || 'Unable to load migration status'}
          </p>
        )}
        {migrationStatus?.data?.status && (
          <div className="bg-muted p-4 rounded-md space-y-4" aria-live="polite">
            <div className="flex items-center justify-between">
              <span className="font-medium">
                {migrationStatus.data.status === 'pending' && 'Waiting to start...'}
                {migrationStatus.data.status === 'running' && 'Migration in progress'}
                {migrationStatus.data.status === 'completed' && 'Migration complete'}
                {migrationStatus.data.status === 'failed' && 'Migration failed'}
                {migrationStatus.data.status === 'cancelled' && 'Migration cancelled'}
              </span>
              {progressKnown && (
                <span className="text-sm">
                  {migrationStatus.data.progress_pct ?? 0}%
                </span>
              )}
            </div>

            {progressKnown ? (
              <Progress aria-label="Migration progress" value={migrationStatus.data.progress_pct ?? 0} className="h-2" />
            ) : running ? (
              <p className="text-sm">
                {status === 'pending' ? 'The migration will start shortly.' : 'Copying and verifying content…'}
              </p>
            ) : null}

            <div className="text-sm space-y-1">
              {migrationStatus.data.current_table && (
                <div>
                  <span className="text-muted-foreground">Current table: </span>
                  <span className="font-mono">{migrationStatus.data.current_table}</span>
                </div>
              )}
              {(progressKnown || (migrationStatus.data.total_rows_migrated ?? 0) > 0) && <div>
                <span className="text-muted-foreground">Rows migrated: </span>
                <span>
                  {(migrationStatus.data.total_rows_migrated ?? 0).toLocaleString()}
                  {migrationStatus.data.total_rows_expected
                    ? ` / ${migrationStatus.data.total_rows_expected.toLocaleString()}`
                    : ''}
                </span>
              </div>}
              {migrationStatus.data.tables_completed && migrationStatus.data.tables_completed.length > 0 && (
                <div>
                  <span className="text-muted-foreground">Tables completed: </span>
                  <span>{migrationStatus.data.tables_completed.length}</span>
                </div>
              )}
            </div>

            {migrationStatus.data.error_message && (
              <div className="text-destructive text-sm" role="alert">
                {migrationStatus.data.error_message}
              </div>
            )}

            {(migrationStatus.data.status === 'pending' || migrationStatus.data.status === 'running') && (
              <Button
                variant="destructive"
                size="sm"
                onClick={handleCancelMigration}
                disabled={cancelMigration.isPending || !review?.permissions.migrate}
              >
                {cancelMigration.isPending ? 'Cancelling...' : 'Cancel Migration'}
              </Button>
            )}
          </div>
        )}

        {!running && (
          <>
            <div className="bg-destructive/10 border border-destructive/20 text-foreground p-4 rounded-md">
              <strong className="text-destructive">Warning:</strong> This operation will:
              <ul className="mt-2 ml-4 list-disc space-y-1">
                <li>Copy and verify this site's content tables, custom fields, and forms</li>
                <li>Switch hosted editing and serving to the destination database</li>
                <li>Retain a source copy; future edits use only the destination</li>
                <li>Keep media files, theme assets, and account settings in their current locations</li>
              </ul>
            </div>

            <div className="flex gap-2 pt-4">
              <Button
                onClick={async () => {
                  if (review?.can_execute && await confirmDialog('Switch this site to the selected server database? Editing and submissions pause; intake is not buffered. A verified private recovery snapshot precedes cutover. Source retention alone is not lossless rollback, and future edits are not synchronized back.')) {
                    handleMigrate();
                  }
                }}
                disabled={!selectedConnectionId || migrateSiteDb.isPending || running || myJobId !== null || !review?.can_execute || !review.review_fingerprint || !review.permissions.migrate || !maintenanceAck || (review.maintenance.external_writers_ack_required !== false && !writersAck) || preflight.isFetching || !!preflight.error || !!statusError}
              >
                {migrateSiteDb.isPending ? 'Starting...' : 'Migrate'}
              </Button>
              <Button
                variant="secondary"
                onClick={() => navigate({ to: '/sites/$siteSlug/settings', params: { siteSlug } })}
                disabled={migrateSiteDb.isPending}
              >
                Cancel
              </Button>
            </div>
          </>
        )}
      </div>

      <UnsavedGuard dirty={showAddModal && !!(newDbName || connectionString || password || sshPassword)} noun="database connection" />
      {/* Add Database Modal */}
      <Dialog open={showAddModal} onOpenChange={(open) => !createConnection.isPending && setShowAddModal(open)}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-auto">
          <DialogHeader>
            <DialogTitle>Add Database Connection</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Connection Name</Label>
              <Input
                type="text"
                value={newDbName}
                onChange={(e) => setNewDbName(e.target.value)}
                placeholder="e.g., Production Supabase"
              />
            </div>

            <div className="space-y-2">
              <Label>Database Type</Label>
              <Select modal={false} value={newDbType} onValueChange={(v: string) => setNewDbType(v as DbType)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="postgres">PostgreSQL</SelectItem>
                  <SelectItem value="mysql">MySQL</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {newDbType !== 'sqlite' && (
              <>
                <div className="space-y-2">
                  <Label>Connection Mode</Label>
                  <div className="flex gap-4 flex-wrap">
                    <label className="flex items-center gap-1 text-sm">
                      <input
                        type="radio"
                        checked={connectionMode === 'supabase'}
                        onChange={() => setConnectionMode('supabase')}
                        className="rounded-full"
                      />
                      Supabase
                    </label>
                    <label className="flex items-center gap-1 text-sm">
                      <input
                        type="radio"
                        checked={connectionMode === 'connection_string'}
                        onChange={() => setConnectionMode('connection_string')}
                        className="rounded-full"
                      />
                      Connection String
                    </label>
                    <label className="flex items-center gap-1 text-sm">
                      <input
                        type="radio"
                        checked={connectionMode === 'connection_string_ssh'}
                        onChange={() => setConnectionMode('connection_string_ssh')}
                        className="rounded-full"
                      />
                      Connection String + SSH
                    </label>
                  </div>
                </div>

                {connectionMode === 'supabase' ? (
                  <>
                    <div className="space-y-2">
                      <Label>Project Reference</Label>
                      <Input
                        type="text"
                        value={projectRef}
                        onChange={(e) => setProjectRef(e.target.value)}
                        placeholder="abcdefghijklmnop"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Database Password</Label>
                      <Input
                        type="password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Region</Label>
                      <Select modal={false} value={region} onValueChange={setRegion}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="us-east-1">US East (N. Virginia)</SelectItem>
                          <SelectItem value="us-west-1">US West (N. California)</SelectItem>
                          <SelectItem value="eu-west-1">EU (Ireland)</SelectItem>
                          <SelectItem value="eu-central-1">EU (Frankfurt)</SelectItem>
                          <SelectItem value="ap-southeast-1">Asia Pacific (Singapore)</SelectItem>
                          <SelectItem value="ap-northeast-1">Asia Pacific (Tokyo)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Connection Mode</Label>
                      <div className="flex gap-4">
                        <label className="flex items-center gap-1 text-sm">
                          <input
                            type="radio"
                            checked={usePooler}
                            onChange={() => setUsePooler(true)}
                            className="rounded-full"
                          />
                          Session Pooler (IPv4)
                        </label>
                        <label className="flex items-center gap-1 text-sm">
                          <input
                            type="radio"
                            checked={!usePooler}
                            onChange={() => setUsePooler(false)}
                            className="rounded-full"
                          />
                          Direct (IPv6)
                        </label>
                      </div>
                    </div>
                    {supabasePreviewUrl && (
                      <div className="bg-muted p-3 rounded-md text-sm font-mono break-all">
                        {supabasePreviewUrl}
                      </div>
                    )}
                  </>
                ) : connectionMode === 'connection_string_ssh' ? (
                  <>
                    <div className="space-y-2">
                      <Label>Connection String</Label>
                      <Input
                        type="password"
                        value={connectionString}
                        onChange={(e) => setConnectionString(e.target.value)}
                        placeholder="postgres://user:pass@host:5432"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="db-database-name-ssh">Database Name</Label>
                      <Input
                        id="db-database-name-ssh"
                        placeholder="my_database"
                        value={databaseName}
                        onChange={(e) => setDatabaseName(e.target.value)}
                      />
                      <p className="text-xs text-muted-foreground">
                        Appended to your connection string. Leave empty if the database name is already included above.
                      </p>
                    </div>
                    {databaseName && (
                      <p className="text-xs text-muted-foreground">
                        Note: Database provisioning is not available for SSH tunnel connections. Ensure the database already exists on the server.
                      </p>
                    )}
                    <div className="bg-muted p-3 rounded-md space-y-3">
                      <div className="font-medium text-sm">SSH Tunnel</div>
                      <div className="space-y-2">
                        <Label>SSH Host</Label>
                        <Input
                          type="text"
                          value={sshHost}
                          onChange={(e) => setSshHost(e.target.value)}
                          placeholder="server.example.com"
                        />
                      </div>
                      <div className="flex gap-2">
                        <div className="flex-1 space-y-2">
                          <Label>SSH Username</Label>
                          <Input
                            type="text"
                            value={sshUsername}
                            onChange={(e) => setSshUsername(e.target.value)}
                            placeholder="root"
                          />
                        </div>
                        <div className="w-20 space-y-2">
                          <Label>Port</Label>
                          <Input
                            type="text"
                            value={sshPort}
                            onChange={(e) => setSshPort(e.target.value)}
                            placeholder="22"
                          />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label>SSH Password</Label>
                        <Input
                          type="password"
                          value={sshPassword}
                          onChange={(e) => setSshPassword(e.target.value)}
                        />
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="space-y-2">
                      <Label>Connection String</Label>
                      <Input
                        type="password"
                        value={connectionString}
                        onChange={(e) => setConnectionString(e.target.value)}
                        placeholder="postgres://user:pass@host:5432"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="db-database-name">Database Name</Label>
                      <Input
                        id="db-database-name"
                        placeholder="my_database"
                        value={databaseName}
                        onChange={(e) => setDatabaseName(e.target.value)}
                      />
                      <p className="text-xs text-muted-foreground">
                        Appended to your connection string. Leave empty if the database name is already included above.
                      </p>
                    </div>
                    {databaseName && (
                      <div className="space-y-2 rounded border p-3">
                        <div className="flex items-center gap-2">
                          <Checkbox
                            id="create-db"
                            checked={createDbIfNotExists}
                            onCheckedChange={(checked) => setCreateDbIfNotExists(!!checked)}
                          />
                          <Label htmlFor="create-db" className="font-normal cursor-pointer">
                            Create database if it doesn't exist
                          </Label>
                        </div>
                        <div className="flex items-center gap-2">
                          <Checkbox
                            id="clear-db"
                            checked={clearExistingDb}
                            onCheckedChange={(checked) => setClearExistingDb(!!checked)}
                          />
                          <Label htmlFor="clear-db" className="font-normal text-destructive cursor-pointer">
                            Wipe existing database (destructive — drops and recreates)
                          </Label>
                        </div>
                        {clearExistingDb ? <label className="grid gap-1 text-sm">Type {databaseName} to confirm permanent replacement<Input aria-label="Confirm database replacement name" value={wipeConfirmation} onChange={(event) => setWipeConfirmation(event.target.value)} autoComplete="off" /></label> : null}
                      </div>
                    )}
                  </>
                )}
              </>
            )}

            <div className="flex gap-2 pt-4">
              <Button
                onClick={handleAddDatabase}
                disabled={createConnection.isPending || !review?.permissions.manage_destinations || (clearExistingDb && wipeConfirmation !== databaseName)}
              >
                {createConnection.isPending ? 'Saving...' : 'Save Connection'}
              </Button>
              <Button
                variant="secondary"
                onClick={() => setShowAddModal(false)}
                disabled={createConnection.isPending || !review?.permissions.manage_destinations || (clearExistingDb && wipeConfirmation !== databaseName)}
              >
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
